import { UrlParamName } from "search-params";

/**
 * Reads the fontUrl query parameter and injects a <link rel="stylesheet"> into <head>.
 * Should be called early in startup, before React renders, so fonts begin loading immediately.
 */
function loadFontStylesheet() {
    const params = new URLSearchParams(window.location.search);
    const fontUrl = params.get(UrlParamName.FONT_URL);

    if (!fontUrl) return;

    try {
        const url = new URL(fontUrl);

        if (url.protocol !== "https:") {
            console.warn("[Caspeco Booking] fontUrl must be an HTTPS URL");
            return;
        }

        // Avoid duplicate injection
        if (document.querySelector(`link[href="${CSS.escape(fontUrl)}"]`)) return;

        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = fontUrl;
        document.head.appendChild(link);
    } catch {
        console.warn("[Caspeco Booking] Invalid fontUrl:", fontUrl);
    }
}

if (import.meta.vitest) {
    const { describe, it, expect, beforeEach, vi } = import.meta.vitest;

    describe("loadFontStylesheet", () => {
        const originalLocation = window.location;

        function setSearchParam(value: string | null): void {
            const baseUrl = `${originalLocation.protocol}//${originalLocation.host}${originalLocation.pathname}`;
            const search =
                value === null ? "" : `?${UrlParamName.FONT_URL}=${encodeURIComponent(value)}`;
            window.history.replaceState({}, "", `${baseUrl}${search}`);
        }

        beforeEach(() => {
            document.head.innerHTML = "";
            setSearchParam(null);
            vi.restoreAllMocks();
        });

        it("does nothing when fontUrl query parameter is missing", () => {
            setSearchParam(null);

            loadFontStylesheet();

            const links = document.head.querySelectorAll('link[rel="stylesheet"]');
            expect(links.length).toBe(0);
        });

        it("logs a warning and does not inject a link for an invalid URL", () => {
            const warnSpy = vi.spyOn(console, "warn");
            setSearchParam("not-a-valid-url");

            loadFontStylesheet();

            const links = document.head.querySelectorAll('link[rel="stylesheet"]');
            expect(links.length).toBe(0);
            expect(warnSpy).toHaveBeenCalledTimes(1);
            expect(warnSpy.mock.calls[0]?.[0]).toBe("[Caspeco Booking] Invalid fontUrl:");
        });

        it("logs a warning and does not inject a link when protocol is not HTTPS", () => {
            const warnSpy = vi.spyOn(console, "warn");
            setSearchParam("http://example.com/fonts.css");

            loadFontStylesheet();

            const links = document.head.querySelectorAll('link[rel="stylesheet"]');
            expect(links.length).toBe(0);
            expect(warnSpy).toHaveBeenCalledTimes(1);
            expect(warnSpy.mock.calls[0]?.[0]).toBe(
                "[Caspeco Booking] fontUrl must be an HTTPS URL"
            );
        });

        it("injects a stylesheet link for a valid HTTPS URL", () => {
            setSearchParam("https://example.com/fonts.css");

            loadFontStylesheet();

            const links = document.head.querySelectorAll('link[rel="stylesheet"]');
            expect(links.length).toBe(1);
            const link = links[0];
            expect(link?.getAttribute("rel")).toBe("stylesheet");
            expect(link?.getAttribute("href")).toBe("https://example.com/fonts.css");
        });

        it("does not inject duplicate stylesheet links for the same URL", () => {
            setSearchParam("https://example.com/fonts.css");

            loadFontStylesheet();
            loadFontStylesheet();

            const links = document.head.querySelectorAll(
                'link[rel="stylesheet"][href="https://example.com/fonts.css"]'
            );
            expect(links.length).toBe(1);
        });
    });
}

export { loadFontStylesheet };
